import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ExternalLink, Star, Clock, Rocket } from "lucide-react";

const STATUS_COLORS = {
  live: "text-green-400 border-green-400/30 bg-green-400/10",
  upcoming: "text-yellow-400 border-yellow-400/30 bg-yellow-400/10",
  pending: "text-zinc-400 border-zinc-400/30 bg-zinc-400/10",
};

const FILTERS = ["All", "Live", "Upcoming"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = projects.filter((p) => {
    if (filter === "All") return p.status !== "pending";
    return p.status === filter.toLowerCase();
  });

  const featured = filtered.filter((p) => p.featured);
  const regular = filtered.filter((p) => !p.featured);

  return (
    <div className="pt-10 pb-20 max-w-[1400px] mx-auto px-6 md:px-10">

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2">
        <div>
          <h1 className="font-display font-bold text-5xl md:text-6xl text-[var(--text-primary)] tracking-tighter">
            Crypto Projects
          </h1>
          <p className="text-[var(--text-secondary)] mt-2">Upcoming and live crypto projects curated by MediaCrypto.</p>
        </div>
        <Link
          to="/projects/apply"
          className="flex-shrink-0 bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition text-sm"
        >
          Submit Project →
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mt-8 mb-10">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "px-4 py-1.5 text-xs font-mono uppercase tracking-widest border transition " +
              (filter === f
                ? "bg-yellow-400 text-black border-yellow-400"
                : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")
            }
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[var(--text-tertiary)] font-mono">Loading projects...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Rocket className="w-12 h-12 text-[var(--text-tertiary)] mx-auto mb-4" />
          <p className="text-[var(--text-tertiary)] font-mono">No projects listed yet.</p>
          <Link to="/projects/apply" className="mt-4 inline-block text-yellow-400 hover:underline text-sm">
            Be the first to submit →
          </Link>
        </div>
      ) : (
        <>
          {/* Featured */}
          {featured.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="font-mono text-xs uppercase tracking-widest text-[var(--text-tertiary)]">Featured</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featured.map((p) => <ProjectCard key={p.id} project={p} featured />)}
              </div>
            </div>
          )}

          {/* All projects */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regular.map((p) => <ProjectCard key={p.id} project={p} />)}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div className="mt-16 border border-[var(--border-soft)] p-4 bg-[var(--bg-elevated)]">
        <p className="text-xs font-mono text-[var(--text-tertiary)] text-center leading-relaxed">
          MediaCrypto lists projects for informational purposes only. We do not endorse, guarantee, or take responsibility for any project listed. Crypto presales and new projects carry significant risk including total loss of investment. Always do your own research before investing.
        </p>
      </div>

      {/* CTA banner */}
      <div className="mt-20 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8 md:p-12 text-center">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-[var(--text-primary)] mb-3">
          Want your project listed?
        </h2>
        <p className="text-[var(--text-secondary)] mb-6 max-w-lg mx-auto">
          Submit your project for review. Featured listings get a dedicated article, social promotion, and top placement.
        </p>
        <Link to="/projects/apply">
          <button className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-3 hover:bg-yellow-500 transition">
            Apply for listing →
          </button>
        </Link>
      </div>
    </div>
  );
}

function ProjectCard({ project, featured }) {
  return (
    <div className={
      "bg-[var(--bg-panel)] border transition p-6 flex flex-col " +
      (featured ? "border-yellow-400/40" : "border-[var(--border-soft)] hover:border-yellow-400/30")
    }>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          {project.logo_url ? (
            <img src={project.logo_url} alt={project.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
              <span className="text-yellow-400 font-bold text-lg">{project.name[0]}</span>
            </div>
          )}
          <div>
            <h3 className="font-display font-bold text-lg text-[var(--text-primary)]">{project.name}</h3>
            {project.ticker && (
              <span className="text-xs font-mono text-[var(--text-tertiary)]">${project.ticker}</span>
            )}
          </div>
        </div>
        <span className={
          "text-xs font-mono uppercase tracking-widest px-2 py-1 border rounded-sm flex-shrink-0 " +
          (STATUS_COLORS[project.status] || STATUS_COLORS.pending)
        }>
          {project.status}
        </span>
      </div>

      <p className="text-sm text-[var(--text-secondary)] mb-5 flex-1 leading-relaxed">
        {project.description}
      </p>

      <div className="flex items-center justify-between pt-4 border-t border-[var(--border-soft)]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-mono px-2 py-1 bg-[var(--bg-elevated)] border border-[var(--border-soft)] text-[var(--text-tertiary)]">
            {project.category}
          </span>
          {project.launch_date && (
            <span className="flex items-center gap-1 text-xs font-mono text-[var(--text-tertiary)]">
              <Clock className="w-3 h-3" /> {project.launch_date}
            </span>
          )}
        </div>
        {project.website && (
          <a
            href={project.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-mono text-yellow-400 hover:underline flex-shrink-0"
          >
            Visit <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}